import { useStyletron, withStyle } from "baseui";
import { ListItem, ListItemLabel } from "baseui/list";
import { VscOrganization } from "react-icons/vsc";
import { Button } from "baseui/button";
import { ChevronRight, Spinner } from "baseui/icon";
import { HorizontalStackPanel } from "../../components/Layout/StackPanel";
import { useGetResourceGroupByIdQuery } from "../../services/resourceGroups/resourceGroupApi";
import { ParagraphXSmall } from "baseui/typography";
import { StyledLink } from "baseui/link";
import { useGetTenantsResourceGroupQuery } from "../../services/resourceGroups/tenantApi";
import { Avatar } from "baseui/avatar";
import { useGetUsersQuery } from "../../services/users/userApi";
import { PiUsers } from "react-icons/pi";
import { Badge } from "baseui/badge";
import { useGetSubscriptionOwnerQuery } from "../../services/resourceGroups/subscriptionApi";
import { ICompany } from "@oqdo/automationapps/Agents/Organizations/Company";
import { IResourceGroup } from "@oqdo/automationapps/Memberships/ResourceGroup";
import { FormattedMessage } from "react-intl";
import TenantAddress from "../tenant/TenantAddress";

const Tenant = (props: { subscriptionId: string; tenant: ICompany; hideResourceGroups?: boolean }) => {
  const { tenant: item, subscriptionId } = props;
  const [css, theme] = useStyletron();
  const { data: users } = useGetUsersQuery({ tenantId: item.id });

  const { data: owner, isSuccess: ownerLoaded } = useGetSubscriptionOwnerQuery(subscriptionId, {
    skip: !subscriptionId,
  });

  return (
    <>
      {item && (
        <ListItem
          overrides={{
            Root: {
              style: ({ $theme }) => ({
                backgroundColor: item.disabled ? $theme.colors.backgroundSecondary : undefined,
              }),
            },
          }}
          shape="ROUND"
          artwork={() => (
            <>
              {item.logo ? (
                <Avatar
                  name={item.name}
                  size="scale800"
                  overrides={{
                    Avatar: {
                      style: ({ $theme }) => ({
                        height: "auto",
                      }),
                    },
                  }}
                  src={item?.logo}
                />
              ) : (
                <VscOrganization color={theme.colors.primary} />
              )}
            </>
          )}
          key={item.id}
          endEnhancer={() => (
            <>
              <HorizontalStackPanel $flexWrap={"nowrap"}>
                <ParagraphXSmall margin={0}>
                  <PiUsers /> <b>{users?.length}</b> {"users"}
                </ParagraphXSmall>
              </HorizontalStackPanel>
              <Button
                $as="a"
                href={`/subscriptions/${subscriptionId}/tenants/${item.id}`}
                kind="tertiary"
                shape="circle">
                <ChevronRight size="scale800" />
              </Button>
            </>
          )}>
          <ListItemLabel
            description={
              <>
                <TenantAddress address={item.address} />
                {!props.hideResourceGroups && (
                  <HorizontalStackPanel $flexWrap={"wrap"}>
                    <ResourceGroups subscriptionId={subscriptionId} tenantId={item.id} />
                  </HorizontalStackPanel>
                )}
              </>
            }>
            <HorizontalStackPanel $alignItems={"center"}>
              {item.name}
              {ownerLoaded && owner && owner.id === item.id && (
                <Badge hierarchy="primary" content="Owner" shape={"pill"} />
              )}
              {item.disabled && <Badge content="Disabled" color="primary" shape="pill" />}
            </HorizontalStackPanel>
          </ListItemLabel>
        </ListItem>
      )}
    </>
  );
};

export default Tenant;

const ResourceGroups = (props: { tenantId: string; subscriptionId: string }) => {
  const { tenantId, subscriptionId } = props;
  const [css, theme] = useStyletron();

  const { data: allowedResourceGroups } = useGetTenantsResourceGroupQuery(props.tenantId);
  const hasRgs = allowedResourceGroups && allowedResourceGroups.length > 0;

  if (!hasRgs)
    return (
      <>
        <HorizontalStackPanel>
          <ParagraphXSmall margin={0} className={css({ color: theme.colors.primary400 })}>
            No access to any resource groups
          </ParagraphXSmall>
        </HorizontalStackPanel>
      </>
    );
  return (
    <>
      <HorizontalStackPanel $flexWrap={"wrap"}>
        <ParagraphXSmall margin={0} className={css({ color: theme.colors.primary600 })}>
          <FormattedMessage id={"hasAcccessTo"} defaultMessage={"Has access to"} />
        </ParagraphXSmall>
        {allowedResourceGroups?.map((rg: IResourceGroup, index) => (
          <ResourceGroup tenantId={tenantId} key={rg.id} subscriptionId={subscriptionId} resourceGroupId={rg.id} />
        ))}
      </HorizontalStackPanel>
    </>
  );
};

const ResourceGroup = (props: { subscriptionId: string; resourceGroupId: string; tenantId: string }) => {
  const { subscriptionId, resourceGroupId, tenantId } = props;
  const { data, isLoading } = useGetResourceGroupByIdQuery(resourceGroupId);

  return (
    <>
      {isLoading && <Spinner $size="10px" />}
      {data && (
        <div>
          <Link animateUnderline href={"/subscriptions/" + subscriptionId + "/resourceGroups/" + resourceGroupId}>
            {data?.name}
          </Link>
        </div>
      )}
    </>
  );
};

const Link = withStyle(StyledLink, ({ $theme }) => ({
  fontWeight: "normal",
  fontSize: $theme.typography.ParagraphXSmall.fontSize,
}));
